/**
 * Jediné místo v celém frontendu, které smí volat `setCanvas` — všechny
 * STRUKTURÁLNÍ canvas mutace musí projít `execute(command)` (viz
 * `commands.ts` hlavička). Výjimka: `updateViewSettings` (Epic 8) — zobrazovací
 * přepínače jsou čistě klientská render-time konfigurace, ne „vratná operace"
 * ve smyslu Epic 13 (epic-13 kap. 2 vyjmenovává jako undo-scope jen Epic
 * 4/5/6/7, Epic 8 mezi nimi záměrně není), takže se nezaznamenávají do
 * undo/redo zásobníku — jen do stejného debounced autosave.
 *
 * Epic 13: `execute()` navíc po aplikaci zaregistruje command do sdíleného
 * undo/redo zásobníku (`command/stack.tsx`) přes `stack.record()`. Samotné
 * undo/redo nejde přes `execute()` (to by se znovu zaznamenalo do zásobníku
 * — nekonečná smyčka), ale přes `applyDirect()`, registrovaný jako "applier"
 * pro store `'canvas'`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CanvasMetadata, ViewSettings } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCommandStackInstance } from '../command/stack';
import type { Command } from './commands';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface CanvasStoreValue {
  canvas: CanvasMetadata;
  execute: (command: Command) => void;
  /** Epic 8 — mimo undo/redo zásobník, viz hlavička souboru. */
  updateViewSettings: (next: ViewSettings) => void;
  saveStatus: SaveStatus;
  /** E3-US1 — explicitní „Uložit" navíc k automatickému autosave; vynutí okamžitý zápis. */
  saveNow: () => void;
}

const CanvasStoreContext = createContext<CanvasStoreValue | null>(null);

/** Debounced autosave — čeká na klid v editaci, ne na každý dílčí command. */
const AUTOSAVE_DEBOUNCE_MS = 800;

interface CanvasStoreProviderProps {
  projectId: string;
  initialCanvas: CanvasMetadata;
  children: ReactNode;
}

export function CanvasStoreProvider({
  projectId,
  initialCanvas,
  children,
}: CanvasStoreProviderProps) {
  const [canvas, setCanvas] = useState(initialCanvas);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const stack = useCommandStackInstance();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<CanvasMetadata | null>(null);
  // Nejvýš jeden PUT /canvas najednou — dva souběžné requesty na stejný projekt
  // mohou na Windows kolidovat v atomickém zápisu (EPERM při rename temp souboru).
  const savingRef = useRef(false);

  const runPersist = useCallback(async () => {
    if (savingRef.current) return;
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    savingRef.current = true;
    setSaveStatus('saving');
    try {
      await api.updateCanvas(projectId, next);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      savingRef.current = false;
      // Mezitím mohla dorazit novější změna — ulož ji hned, ne až po dalším debounce.
      if (pendingRef.current) void runPersist();
    }
  }, [projectId]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void runPersist();
  }, [runPersist]);

  const schedulePersist = useCallback(
    (next: CanvasMetadata) => {
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // Nedokončená debounced úprava by se ztratila při přepnutí tabu/zavření okna.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [flush]);

  const execute = useCallback(
    (command: Command) => {
      setCanvas((prev) => {
        const next = command.apply(prev);
        schedulePersist(next);
        return next;
      });
      stack.record('canvas', command);
    },
    [schedulePersist, stack],
  );

  // Applier pro undo/redo (Epic 13) — na rozdíl od `execute()` NEvolá `stack.record`,
  // jinak by se vracení/opakování commandu samo znovu zapsalo do zásobníku.
  const applyDirect = useCallback(
    (command: Command, direction: 'forward' | 'backward') => {
      setCanvas((prev) => {
        const next = direction === 'forward' ? command.apply(prev) : command.revert(prev);
        schedulePersist(next);
        return next;
      });
    },
    [schedulePersist],
  );

  useEffect(() => {
    stack.setApplier('canvas', applyDirect);
    return () => stack.setApplier('canvas', null);
  }, [stack, applyDirect]);

  const updateViewSettings = useCallback(
    (next: ViewSettings) => {
      setCanvas((prev) => {
        const nextCanvas = { ...prev, viewSettings: next };
        schedulePersist(nextCanvas);
        return nextCanvas;
      });
    },
    [schedulePersist],
  );

  const value = useMemo<CanvasStoreValue>(
    () => ({ canvas, execute, updateViewSettings, saveStatus, saveNow: flush }),
    [canvas, execute, updateViewSettings, saveStatus, flush],
  );

  return <CanvasStoreContext.Provider value={value}>{children}</CanvasStoreContext.Provider>;
}

export function useCanvasStore(): CanvasStoreValue {
  const context = useContext(CanvasStoreContext);
  if (!context) throw new Error('useCanvasStore musí být uvnitř CanvasStoreProvider.');
  return context;
}
